/* ============================================================
   juno-slider.js — het custom element waar de CSS van het origineel aan hangt.

   Het origineel draait Embla. Wij bouwen het gedrag zelf na, want een
   afhankelijkheid meeslepen voor een balk met drie regels is niet nodig — maar
   de NAAM en de klassen moeten kloppen: slider.css stuurt op `juno-slider`,
   `.slider__viewport`, `.slider__track`, `.slider__item` en op `.is-active`,
   dat pas op de track komt zodra de slider echt actief is.

   Attributen (zoals in het origineel):
     enabled="true" | enabled="(max-width: 1024px)"   wanneer de slider slidet
     autoPlay="2500"                                  ms tussen twee slides
     wheelGestures                                    horizontaal scrollen

   Onder `prefers-reduced-motion: reduce` schuift er niets vanzelf: dan staan
   de slides er gewoon, en blijft alles bereikbaar met tab en pijltjes.
   ============================================================ */
class JunoSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-slider="viewport"], .slider__viewport');
    this.track = this.querySelector('.slider__track');
    if (!this.viewport || !this.track) return;

    this.items = [...this.track.children];
    this.index = 0;
    this.rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const wanneer = this.getAttribute('enabled') || 'true';
    this.mq = wanneer === 'true' || wanneer === '' ? null : window.matchMedia(wanneer);
    this.aan = this.mq ? this.mq.matches : true;
    if (this.mq) this.mq.addEventListener('change', (e) => this.zet(e.matches));

    this.stippenBouw();
    this.zet(this.aan);
    this.sleepAan();

    // Een balk die vanzelf doorschuift moet aankondigen wat er verandert,
    // anders mist een schermlezer de tweede en derde melding.
    if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
  }

  disconnectedCallback() { this.stop(); }

  zet(aan) {
    this.aan = aan;
    /* is-active hoort op de VIEWPORT, niet op de track: de CSS van het origineel
       stuurt met `.is-active .slider__item` op de kinderen, en met de klasse op
       de track zelf raakt die selector niets. */
    this.viewport.classList.toggle('is-active', aan);
    if (this.stippen) this.stippen.hidden = !aan;
    if (aan) this.start(); else { this.stop(); this.naar(0); }
  }

  start() {
    const ms = parseFloat(this.getAttribute('autoPlay') || this.getAttribute('autoplay') || '0');
    this.stop();
    if (!ms || this.rustig || this.items.length < 2) return;
    this.timer = setInterval(() => this.naar(this.index + 1), ms);
    // Stilstaan zolang iemand kijkt of leest.
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
  }

  stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }

  /* ---------- stippen ----------
     Alleen met het attribuut `dots`, en alleen bij meer dan één slide. Op een
     telefoon staat er één foto per keer in beeld en is er anders niets dat zegt
     dat er nog een tweede is; de halve foto die het origineel liet meekijken is
     bij twee beelden geen hint maar een beeld dat niet aansluit. De stippen
     tonen de echte stand — ze zijn geen versiering. */
  stippenBouw() {
    if (!this.hasAttribute('dots') || this.items.length < 2) return;
    const lijst = document.createElement('div');
    lijst.className = 'slider__dots';
    lijst.setAttribute('role', 'tablist');
    lijst.setAttribute('aria-label', 'Beelden');
    this.items.forEach((_, i) => {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'slider__dot';
      knop.setAttribute('role', 'tab');
      knop.setAttribute('aria-label', `Beeld ${i + 1} van ${this.items.length}`);
      knop.addEventListener('click', () => { this.stop(); this.naar(i); this.start(); });
      lijst.appendChild(knop);
    });
    this.appendChild(lijst);
    this.stippen = lijst;
    this.stippenBij();
  }

  stippenBij() {
    if (!this.stippen) return;
    [...this.stippen.children].forEach((knop, i) => {
      const actief = i === this.index;
      knop.classList.toggle('is-actief', actief);
      knop.setAttribute('aria-selected', actief ? 'true' : 'false');
    });
  }

  naar(i) {
    if (!this.items.length) return;
    this.index = (i + this.items.length) % this.items.length;
    /* De stap is de afstand tussen twee slides, niet de breedte van één slide:
       de track heeft een `--col-gap` van 16 px. Met alleen de breedte schoof de
       galerij per foto 16 px scheef, en dat stapelt op bij elke volgende. */
    const eerste = this.items[0].getBoundingClientRect();
    const tweede = this.items[1] && this.items[1].getBoundingClientRect();
    const stap = tweede ? tweede.left - eerste.left : eerste.width;
    this.track.style.transform = this.aan
      ? `translate3d(${-this.index * stap}px, 0, 0)`
      : '';
    this.track.style.transition = this.rustig ? 'none' : 'transform .5s cubic-bezier(.4,0,.2,1)';
    this.stippenBij();
  }

  sleepAan() {
    let start = null, verschoven = 0;
    this.viewport.addEventListener('pointerdown', (e) => {
      if (!this.aan) return;
      start = e.clientX; verschoven = 0;
      this.viewport.classList.add('is-dragging');
      this.stop();
    });
    this.viewport.addEventListener('pointermove', (e) => {
      if (start === null) return;
      verschoven = e.clientX - start;
    });
    const los = () => {
      if (start === null) return;
      this.viewport.classList.remove('is-dragging');
      if (Math.abs(verschoven) > 40) this.naar(this.index + (verschoven < 0 ? 1 : -1));
      start = null;
      this.start();
    };
    this.viewport.addEventListener('pointerup', los);
    this.viewport.addEventListener('pointercancel', los);
    this.viewport.addEventListener('pointerleave', los);
    this.viewport.classList.add('is-draggable');
  }
}
if (!customElements.get('juno-slider')) customElements.define('juno-slider', JunoSlider);
